import React, { useState } from 'react';
import { BrowserRouter as Router, Link, Routes, Route } from 'react-router-dom';
import VFooter from '../components/VFooter'
import Top from './Top'
import { Card } from './ui';
import { FileText, Users, CreditCard, Shield, AlertTriangle, Scale, ChevronDown, ChevronRight, Building, Calendar } from 'lucide-react';

const TermsAndServices = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (index) => {
    setExpandedSection(expandedSection === index ? null : index);
  };

  const termsSections = [
    {
      title: "Platform Agreement & Acceptance",
      icon: <FileText className="w-5 h-5" />,
      content: [
        {
          subtitle: "Agreement to Terms",
          details: "By accessing or using the Vasstr platform, creating an account, or using our services, you automatically agree to be bound by these Terms of Service. If you do not agree to these terms, you must not use our platform."
        },
        {
          subtitle: "Age Requirements",
          details: "You must be at least 18 years old to use this platform. If you are under 18, you may only use this service with parental or guardian consent and supervision."
        },
        {
          subtitle: "Modifications to Terms",
          details: "We reserve the right to modify these terms at any time. Users will be notified of significant changes via email or platform notifications. Continued use after modifications constitutes acceptance of the updated terms."
        },
        {
          subtitle: "Platform Scope",
          details: "These terms apply to all services offered through Vasstr, including but not limited to the marketplace, website builder, payment processing, and designer tools."
        }
      ]
    },
    {
      title: "User Accounts & Registration",
      icon: <Users className="w-5 h-5" />,
      content: [
        {
          subtitle: "Account Creation",
          details: "Users may register as customers or designers. Designers must complete KYC verification including PAN, bank account details, and identity verification before accessing full platform features."
        },
        {
          subtitle: "Account Security",
          details: "You are responsible for maintaining the confidentiality of your login credentials. You must notify us immediately of any unauthorized access or security breach. We are not liable for losses due to credential misuse."
        },
        {
          subtitle: "Accurate Information",
          details: "All information provided during registration must be accurate, current, and complete. False information may result in account suspension or termination."
        },
        {
          subtitle: "Account Verification",
          details: "We may require additional verification for security purposes. Failure to complete verification may limit platform access or result in account suspension."
        }
      ]
    },
    {
      title: "Designer Terms & Responsibilities",
      icon: <Building className="w-5 h-5" />,
      content: [
        {
          subtitle: "Design Ownership & Rights",
          details: "Designers retain full intellectual property rights to their original designs. By uploading content, designers grant Vasstr a license to display, promote, and facilitate sales of their designs on the platform."
        },
        {
          subtitle: "Content Standards",
          details: "All uploaded content must be original, legally owned, and appropriate. Prohibited content includes copyrighted material, offensive imagery, misleading descriptions, or anything violating Indian laws."
        },
        {
          subtitle: "Product Accuracy",
          details: "Designers must provide accurate product descriptions, pricing, fabric details, and delivery timelines. Misleading information may result in order cancellations and account penalties."
        },
        {
          subtitle: "Order Fulfillment",
          details: "Designers are responsible for fulfilling orders within specified timelines, maintaining quality standards, and providing customer support for their products."
        },
        {
          subtitle: "Professional Conduct",
          details: "Designers must maintain professional communication with customers, respond promptly to inquiries, and handle disputes fairly and promptly."
        }
      ]
    },
    {
      title: "Commission & Payment Terms",
      icon: <CreditCard className="w-5 h-5" />,
      content: [
        {
          subtitle: "Platform Commission",
          details: "Vasstr charges a 3% commission on all completed orders. Commission is calculated on the total order value and deducted from designer payouts."
        },
        {
          subtitle: "Payout Processing",
          details: "Designer payouts (97% of order value) are processed automatically within 24-48 hours after order delivery confirmation. Minimum payout threshold is ₹500."
        },
        {
          subtitle: "Payment Methods",
          details: "All payments are processed through Razorpay. Customers can pay via credit/debit cards, UPI, net banking, and other supported methods. Designers receive payouts via bank transfer."
        },
        {
          subtitle: "Refunds & Cancellations",
          details: "Refund eligibility depends on order status and cancellation reasons. Platform fees may not be refunded in certain cases. Disputed transactions will be investigated individually."
        },
        {
          subtitle: "Tax Responsibilities",
          details: "Designers are responsible for their tax obligations including GST registration (if applicable), income tax, and other statutory requirements as per Indian tax laws."
        }
      ]
    },
    {
      title: "Platform Usage & Prohibited Activities",
      icon: <Shield className="w-5 h-5" />,
      content: [
        {
          subtitle: "Acceptable Use",
          details: "Users may only use the platform for legitimate business purposes in accordance with these terms and applicable laws. Commercial use outside the platform's intended purpose is prohibited."
        },
        {
          subtitle: "Prohibited Activities",
          details: "Users must not engage in fraud, spam, harassment, violation of intellectual property rights, circumventing platform fees, or any illegal activities. Violations will result in immediate account termination."
        },
        {
          subtitle: "Content Moderation",
          details: "We reserve the right to review, moderate, or remove any content that violates our policies. Repeated violations may result in account suspension or permanent bans."
        },
        {
          subtitle: "Platform Integrity",
          details: "Users must not attempt to hack, disrupt, or compromise platform security. Any security vulnerabilities discovered should be reported to our technical team."
        }
      ]
    },
    {
      title: "Intellectual Property & Content",
      icon: <FileText className="w-5 h-5" />,
      content: [
        {
          subtitle: "Platform IP Rights",
          details: "All platform technology, code, trademarks, and proprietary content belong to Vasstr. Users are granted limited access rights only for legitimate platform use."
        },
        {
          subtitle: "User Content License",
          details: "By uploading content, users grant Vasstr a worldwide, non-exclusive license to use, display, and promote the content for platform operations and marketing purposes."
        },
        {
          subtitle: "Copyright Compliance",
          details: "Users must respect intellectual property rights. We will respond to valid DMCA takedown notices and may terminate accounts of repeat infringers."
        },
        {
          subtitle: "Design Protection",
          details: "While we implement security measures to protect designs, users are encouraged to take additional precautions for highly sensitive or proprietary content."
        }
      ]
    },
    {
      title: "Limitation of Liability & Disclaimers",
      icon: <AlertTriangle className="w-5 h-5" />,
      content: [
        {
          subtitle: "Service Availability",
          details: "Platform availability is subject to maintenance, updates, and technical issues. We do not guarantee 100% uptime and are not liable for temporary service interruptions."
        },
        {
          subtitle: "Transaction Disputes",
          details: "Vasstr acts as a facilitating platform. Disputes between designers and customers should be resolved directly. We may provide mediation but are not liable for transaction outcomes."
        },
        {
          subtitle: "Limitation of Damages",
          details: "Our liability is limited to the platform fees paid by users in the 12 months preceding any claim. We are not liable for indirect, consequential, or punitive damages."
        },
        {
          subtitle: "Third-Party Services",
          details: "We integrate with third-party services (Razorpay, email providers, etc.). We are not responsible for failures or issues arising from third-party service providers."
        },
        {
          subtitle: "User Content Disclaimer",
          details: "Users are solely responsible for their content, designs, and business practices. Vasstr does not endorse or guarantee the quality, accuracy, or legality of user-generated content."
        }
      ]
    },
    {
      title: "Account Termination & Suspension",
      icon: <AlertTriangle className="w-5 h-5" />,
      content: [
        {
          subtitle: "Termination Rights",
          details: "We reserve the right to suspend or terminate accounts for terms violations, fraudulent activity, or other reasons that may harm the platform or other users."
        },
        {
          subtitle: "User-Initiated Termination",
          details: "Users may deactivate their accounts at any time. Account closure may be subject to completion of pending orders and settlement of outstanding dues."
        },
        {
          subtitle: "Data Retention Post-Termination",
          details: "After account termination, we may retain certain data for legal compliance, dispute resolution, and fraud prevention as outlined in our Privacy Policy."
        },
        {
          subtitle: "Outstanding Obligations",
          details: "Account termination does not release users from outstanding obligations including pending orders, payments, or legal liabilities."
        }
      ]
    }
  ];

  return (
    <div className="h-[100%] ">
      <Top/>
      {/* Main Content */}
      <main className="pt-32">
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#f4ece5] to-[#f8f3ed]">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#ca7751] rounded-full mb-6">
                <Scale className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">Terms & Conditions</h1>
              <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                Welcome to Vasstr. These Terms of Service govern your use of our platform and services. Please read them carefully before using our platform.
              </p>
              {/* <div className="mt-6 text-sm text-gray-500">
                <p><strong>Effective Date:</strong> December 15, 2025</p>
                <p><strong>Last Updated:</strong> December 15, 2025</p>
              </div> */}
            </div>

            {/* Introduction */}
            <div className="bg-white p-8 rounded-xl shadow-lg mb-8 border-l-4 border-[#ca7751]">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Platform Overview</h2>
              <p className="text-gray-600 mb-4">
                Vasstr is a digital marketplace and platform that connects fashion designers with customers, providing tools for e-commerce, website creation, payment processing, and business management. Our platform operates under Indian law and serves users primarily in India.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">By using our platform, you agree to:</h3>
                <ul className="text-gray-600 space-y-1 ml-4">
                  <li>• Comply with all applicable Indian laws and regulations</li>
                  <li>• Provide accurate and truthful information</li>
                  <li>• Respect intellectual property rights</li>
                  <li>• Maintain professional conduct in all interactions</li>
                  <li>• Pay applicable fees and commissions</li>
                </ul>
              </div>
            </div>

            {/* Expandable Sections */}
            <div className="space-y-4">
              {termsSections.map((section, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
                  <button
                    onClick={() => toggleSection(index)}
                    className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-200"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="text-[#ca7751]">
                        {section.icon}
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900">{section.title}</h3>
                    </div>
                    {expandedSection === index ? (
                      <ChevronDown className="w-6 h-6 text-[#ca7751]" />
                    ) : (
                      <ChevronRight className="w-6 h-6 text-[#ca7751]" />
                    )}
                  </button>

                  {expandedSection === index && (
                    <div className="px-8 pb-8">
                      <div className="space-y-6">
                        {section.content.map((item, itemIndex) => (
                          <div key={itemIndex} className="border-l-2 border-[#ca7751] pl-6">
                            <h4 className="font-semibold text-gray-800 mb-2">{item.subtitle}</h4>
                            <p className="text-gray-600 leading-relaxed">{item.details}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Quick Reference */}
            <div className="bg-white p-8 rounded-xl shadow-lg mt-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Reference</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="border-l-4 border-[#ca7751] pl-4">
                  <h4 className="font-semibold text-gray-800 mb-2">Platform Commission</h4>
                  <p className="text-sm text-gray-600">3% on all completed orders</p>
                </div>
                <div className="border-l-4 border-[#ca7751] pl-4">
                  <h4 className="font-semibold text-gray-800 mb-2">Minimum Payout</h4>
                  <p className="text-sm text-gray-600">₹100 for bank transfers</p>
                </div>
                <div className="border-l-4 border-[#ca7751] pl-4">
                  <h4 className="font-semibold text-gray-800 mb-2">Account Requirements</h4>
                  <p className="text-sm text-gray-600">18+ years, valid KYC documents</p>
                </div>
                <div className="border-l-4 border-[#ca7751] pl-4">
                  <h4 className="font-semibold text-gray-800 mb-2">Payout Timeline</h4>
                  <p className="text-sm text-gray-600">6-7 days after reciving the order</p>
                </div>
                <div className="border-l-4 border-[#ca7751] pl-4">
                  <h4 className="font-semibold text-gray-800 mb-2">Design Rights</h4>
                  <p className="text-sm text-gray-600">Designers(person who uploads the design/product) retain full ownership</p>
                </div>
                <div className="border-l-4 border-[#ca7751] pl-4">
                  <h4 className="font-semibold text-gray-800 mb-2">Termination Notice</h4>
                  <p className="text-sm text-gray-600">Immediate for violations</p>
                </div>
              </div>
            </div>

            {/* Contact & Governing Law */}
            <div className="grid md:grid-cols-2 gap-8 mt-8">
              {/* Governing Law */}
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                  <Scale className="w-5 h-5 text-[#ca7751] mr-2" />
                  Governing Law & Jurisdiction
                </h3>
                <div className="text-sm text-gray-600 space-y-2">
                  <p><strong>Governing Law:</strong> These terms are governed by the laws of India, including but not limited to:</p>
                  <ul className="ml-4 space-y-1">
                    <li>• Information Technology Act, 2000</li>
                    <li>• Indian Contract Act, 1872</li>
                    <li>• Consumer Protection Act, 2019</li>
                    <li>• Goods and Services Tax Act, 2017</li>
                  </ul>
                  <p><strong>Jurisdiction:</strong> Any disputes will be subject to the exclusive jurisdiction of the courts in Hyderabad, Telangana, India.</p>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-gradient-to-r from-[#ca7751] to-[#d67e5a] p-6 rounded-xl text-white">
                <h3 className="font-semibold mb-4">Questions About Terms?</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="font-medium">Legal & Compliance:</p>
                    <p className="text-white/90">team@Vasstr.com</p>
                  </div>
                  <div>
                    <p className="font-medium">Phone:</p>
                    <p className="text-white/90">+91 9704387878</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Agreement Acknowledgment */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 p-6 rounded-xl mt-8">
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <FileText className="w-5 h-5 text-blue-600 mr-2" />
                Agreement Acknowledgment
              </h3>
              <p className="text-sm text-gray-600">
                By continuing to use Vasstr, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. These terms constitute a legally binding agreement between you and Vasstr. If you do not agree to these terms, you must discontinue use of our platform immediately.
              </p>
            </div>
          </div>
        </section>
      </main>
      <VFooter/>
    </div>
  );
};
export default TermsAndServices;